// src/components/MobileCategoriesDrawer.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaTimes, FaChevronRight } from "react-icons/fa";

// ✅ Firebase Bağlantıları
import { db } from "../firebase";
import { collection, getDocs } from "firebase/firestore";

export default function MobileCategoriesDrawer({ open = false, onClose = () => {} }) {
  const navigate = useNavigate();

  // ✅ Verileri tutacak state (Sabit dosya yerine)
  const [topList, setTopList] = useState([]);
  const [stack, setStack] = useState([]);

  // Açılış/kapanış animasyonu için ayrı kontrol
  const [internalOpen, setInternalOpen] = useState(Boolean(open));
  const [animateOpen, setAnimateOpen] = useState(Boolean(open));
  const [skipCloseAnim, setSkipCloseAnim] = useState(false);

  // ✅ 1. VERİ ÇEKME (Firebase)
  useEffect(() => {
    // Sadece menü açılacağı zaman ve elimizde veri yoksa çekelim
    if (open && topList.length === 0) {
      const fetchCategories = async () => {
        try {
          const colRef = collection(db, "categories");
          const snapshot = await getDocs(colRef);
          const rawData = snapshot.docs.map((doc) => doc.data());

          // Admin'deki sıralamaya göre diz (Order)
          rawData.sort((a, b) => (a.order || 999) - (b.order || 999));

          // Senin yapına uygun formata çeviriyoruz
          const formatted = rawData.map((t) => ({
            slug: t.slug,
            label: t.label,
            raw: t,
          }));

          setTopList(formatted);
        } catch (error) {
          console.error("Kategori hatası:", error);
        }
      };

      fetchCategories();
    }
  }, [open, topList.length]);

  // Animasyon ve Görünürlük Mantığı
  useEffect(() => {
    let rafId;
    let closeTimer;

    if (open) {
      setSkipCloseAnim(false);
      setInternalOpen(true);

      // Bir frame gecikmeyle aç ki "pat diye" görünmesin
      rafId = requestAnimationFrame(() => {
        setAnimateOpen(true);
      });
    } else {
      setAnimateOpen(false);

      closeTimer = setTimeout(() => {
        setInternalOpen(false);
        setStack([]);
      }, 700); // duration-700 ile uyumlu
    }

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (closeTimer) clearTimeout(closeTimer);
    };
  }, [open]);

  // ✅ Alt Kategorileri Bulma Fonksiyonu (Firebase yapısına uygun)
  const childrenFromRaw = (raw) => {
    if (!raw) return [];

    // Firebase'de alt kategoriler 'children' array'inde tutulur
    if (Array.isArray(raw.children) && raw.children.length > 0) {
      return raw.children.map((c) => ({
        slug: c.slug,
        label: c.label,
        raw: c,
      }));
    }

    // Eski yapı desteği (Groups vs.)
    if (Array.isArray(raw.groups)) {
      return raw.groups.flatMap((g) =>
        (g.items || []).map((it) => ({
          slug: it.slug,
          label: it.label,
          raw: it,
        }))
      );
    }

    return [];
  };

  const handleClose = () => {
    onClose();
  };

  // ✅ Ana Kategori Tıklaması (Level 1 -> Level 2'yi Açar)
  const handleTopClick = (topItem) => {
    const children = childrenFromRaw(topItem.raw);

    // Eğer hiç alt kategori yoksa direkt git
    if (!children || children.length === 0) {
      setSkipCloseAnim(true);
      onClose();
      navigate(`/shop/${topItem.slug}`);
      return;
    }

    // Alt kategori varsa Stack'e ekle (Kayma efekti ile 2. seviyeyi aç)
    setStack((s) => [...s, { levelLabel: topItem.label, items: children, topSlug: topItem.slug }]);
  };

  // ✅ Alt Kategori Tıklaması (Level 2 -> DİREKT GİT)
  // Burası isteğine göre değişti: Alt-Alt kategori olsa bile açmıyoruz, direkt gidiyoruz.
  const handleItemClick = (item) => {
    const topSlug = stack[0]?.topSlug || null;

    setSkipCloseAnim(true);
    onClose();

    // Alt-Alt kategori kontrolü yapmadan direkt navigasyon sağlıyoruz
    if (topSlug) {
      navigate(`/shop/${topSlug}/${item.slug}`);
    } else {
      navigate(`/shop/${item.slug}`);
    }
  };

  const panels = useMemo(
    () => [{ levelLabel: "Kategoriler", items: topList }, ...stack],
    [topList, stack]
  );
  const currentIndex = panels.length - 1;

  if (!open && !internalOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-40 md:hidden transition-all duration-700 ${
        internalOpen ? "pointer-events-auto" : "pointer-events-none"
      }`}
    >
      {/* Arka Plan Overlay */}
      <div
        className={`absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-700 ${
          animateOpen ? "opacity-100" : "opacity-0"
        }`}
        onClick={handleClose}
      />

      {/* Drawer Panel - bottom-[60px] sayesinde Bottom Navbar açık kalır */}
      <div
        className={`absolute left-0 right-0 top-16 bottom-[60px] bg-white rounded-t-2xl shadow-2xl flex flex-col transition-transform duration-700 ease-in-out will-change-transform`}
        style={{
          transform: animateOpen ? "translateY(0)" : "translateY(100%)",
        }}
      >
        {/* Header - Sola Dayalı */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gray-50/60 rounded-t-2xl">
          <div className="flex items-center gap-3 justify-start">
            {stack.length > 0 ? (
              <button
                onClick={() => setStack((s) => s.slice(0, -1))}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-white border border-gray-200 text-gray-600 shadow-sm active:scale-95 transition"
              >
                <FaArrowLeft size={14} />
              </button>
            ) : (
              <div className="w-8"></div>
            )}

            <h3 className="text-lg font-bold text-gray-800 line-clamp-1 text-left">
              {panels[currentIndex]?.levelLabel || "Kategoriler"}
            </h3>
          </div>

          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-white border border-gray-200 text-gray-500 hover:text-red-500 active:scale-95 transition shadow-sm"
          >
            <FaTimes size={15} />
          </button>
        </div>

        {/* İçerik (Kayar Paneller) */}
        <div className="relative flex-1 overflow-hidden bg-white">
          {panels.map((panel, idx) => {
            const offset = (idx - currentIndex) * 100;

            const style = {
              transform: `translateX(${offset}%)`,
              transition: skipCloseAnim
                ? "none"
                : "transform 500ms cubic-bezier(0.4, 0, 0.2, 1)",
            };

            return (
              <div key={idx} className="absolute inset-0 overflow-y-auto" style={style}>
                <div className="pb-10">
                  {panel.items.map((it) => {
                    return (
                      <button
                        key={it.slug}
                        // idx === 0 ise Ana Kategori (Aç), değilse Alt Kategori (Git)
                        onClick={() => (idx === 0 ? handleTopClick(it) : handleItemClick(it))}
                        className="w-full flex items-center justify-between px-5 py-4 hover:bg-purple-50 active:bg-purple-100 transition-colors group border-b border-gray-50 last:border-0"
                      >
                        <div className="flex flex-col items-start">
                          <span className="text-[15px] font-bold text-gray-700 group-hover:text-[#6A0DAD]">
                            {it.label}
                          </span>
                          {it.groupLabel && (
                            <span className="text-[10px] font-semibold text-[#6A0DAD] uppercase tracking-wide opacity-70 mt-0.5">
                              {it.groupLabel}
                            </span>
                          )}
                        </div>

                        {/* Sadece Ana Kategoride (idx 0) Ok İşareti Göster ki içeri girildiği belli olsun */}
                        {idx === 0 && (
                          <FaChevronRight size={14} className="text-gray-300 group-hover:text-[#6A0DAD]" />
                        )}
                      </button>
                    );
                  })}

                  {panel.items.length === 0 && (
                    <div className="text-center p-10 text-gray-400">Yükleniyor veya boş...</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}